import {useFormik} from 'formik'
import EventPage from 'organization/Event/EventPage'
import {EventFaqHelp} from 'organization/Event/Help/FAQ'
import {StreamManagement} from 'organization/Event/Management/StreamManagement'
import StreamProvider from 'organization/Event/Management/StreamProvider'
import {UpdateMeetingData} from 'organization/Event/Management/UpdateMeetingData'
import {useMeeting} from 'organization/Event/MeetingProvider'
import {useOrganizationEvent} from 'organization/Event/OrganizationEventProvider'
import BreadcrumMenu from 'organization/theme/Breadcrum'
import React from 'react'
import {confirmAlert} from 'react-confirm-alert'
import {toast} from 'react-toastify'
import {routes} from 'routes/routes'
import styled from 'styled-components'
import {useI18n} from 'i18n/I18nContext'

export function Management() {
  const {t} = useI18n()
  const {updateMeeting, meeting, getStartLink, loading} = useMeeting()
  const {event} = useOrganizationEvent()

  const {handleSubmit, handleBlur, handleChange, values} = useFormik({
    initialValues: {
      host_first_name: meeting?.host_first_name ?? '',
      host_last_name: meeting?.host_last_name ?? '',
      can_join: meeting?.can_join ?? false,
    },
    onSubmit: async (values: UpdateMeetingData) => {
      const result = await updateMeeting(values)

      if (result === true) {
        toast.success(t('settings.saved'))
      }
    },
  })

  const handleJoin = async () => {
    if (event?.is_live) {
      return join()
    }

    confirmAlert({
      message: t('orgManagement.testRoomMsg'),
      buttons: [
        {
          label: t('orgManagement.start'),
          onClick: () => {
            join()
          },
        },
      ],
    })
  }

  const join = async () => {
    const newTab = window.open(routes.loadingPage, '_blank')

    try {
      const result = await getStartLink()

      if (!result) {
        if (newTab) newTab.close()
        return
      }

      if (newTab) {
        newTab.location.href = result
      }
    } catch (error: any) {
      toast.warning(error?.message || t('orgManagement.errorGeneric'))
      if (newTab) newTab.close()
    }
  }

  return (
    <EventPage>
      <>
        <div className="row pl-30 pb-30 pr-30">
          <BreadcrumMenu
            title={t('orgManagement.breadcrumb')}
            helpInfo={<EventFaqHelp faqKey="managementPage" />}
          />

          <div className="col-xxl-12">
            <div className="create__event-area">
              <div className="body__card-wrapper">
                {/* create event form  */}

                <div className="create__event-main pt-25">
                  <div className="event__left-box">
                    <div className="create__input-wrapper">
                      <form onSubmit={handleSubmit}>
                        <div className="singel__input-field mb-15">
                          <label className="input__field-text">
                            {t('orgManagement.hostFirstName')}
                          </label>

                          <input
                            type="text"
                            name="host_first_name"
                            placeholder={t('orgManagement.hostFirstNamePh')}
                            defaultValue={values.host_first_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                        </div>

                        <div className="singel__input-field mb-15">
                          <label className="input__field-text">
                            {t('orgManagement.hostLastName')}
                          </label>

                          <input
                            type="text"
                            name="host_last_name"
                            placeholder={t('orgManagement.hostLastNamePh')}
                            defaultValue={values.host_last_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                        </div>

                        <div className="singel__input-field mb-15">
                          <label className="input__field-text">
                            {t('orgManagement.canJoinLabel')}
                          </label>

                          <div className={`bd-theme-toggle `}>
                            <label
                              className="bd-theme-toggle-main"
                              style={{width: '100%'}}
                              htmlFor="bd-theme-toggler"
                            >
                              <span className={`bd-theme-toggle-dark `}>
                                {t('orgManagement.toggleOn')}
                              </span>
                              <input
                                name="can_join"
                                type="checkbox"
                                id="bd-theme-toggler"
                                checked={values.can_join}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <i className="bd-theme-toggle-slide"></i>
                              <span className={`bd-theme-toggle-light `}>
                                {t('orgManagement.toggleOff')}
                              </span>
                            </label>
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className={
                            loading
                              ? 'disableBtnStyle w-100 mt-30'
                              : 'input__btn w-100 mt-30'
                          }
                        >
                          {t('orgManagement.save')}
                        </button>
                      </form>
                      <StreamProvider>
                        <StreamManagement />
                      </StreamProvider>
                    </div>
                  </div>

                  <div className="event__right-box">
                    <div className="row g-20">
                      <div className="pricing__item mb-30">
                        <div className="pricing__item-header">
                          <div>
                            <span className="pricing__item-title">
                              {t('orgManagement.startEventTitle')}
                            </span>
                          </div>
                        </div>
                        <div className="pricing__item-content">
                          <ul>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('orgManagement.warnEventStarts')}
                            </li>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('orgManagement.warnJoinIfEnabled')}
                            </li>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('orgManagement.warnVisibleAsHost')}
                            </li>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('orgManagement.beforeGuestsTitle')}
                              <br /> 1. {t('orgManagement.beforeGuests1')}
                              <br /> 2. {t('orgManagement.beforeGuests2')}
                              <br /> 3. {t('orgManagement.beforeGuests3')}
                            </li>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('orgManagement.hostToolsTitle')}
                              <br />
                              {t('orgManagement.hostToolsBody')}
                            </li>
                          </ul>
                        </div>
                        <div className="pricing__btn-wrap">
                          <JoinButton
                            type="button"
                            onClick={handleJoin}
                            disabled={loading}
                            className={
                              loading
                                ? 'disableBtnStyle w-100 mt-30'
                                : 'input__btn w-100 mt-30'
                            }
                          >
                            {t('orgManagement.start')}
                          </JoinButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </EventPage>
  )
}

export const JoinButton = styled.button`
  background: linear-gradient(90deg, #05a95c, #04fc83);

  &.disableBtnStyle {
    background: #2e353a;
  }
`
